import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DollarSignIcon, ClockIcon, XIcon, MailIcon, Share2Icon, LayersIcon, PenToolIcon, GlobeIcon, InstagramIcon, TwitterIcon, PaletteIcon, CheckCircleIcon, XCircleIcon, CheckIcon, StarIcon, MapPinIcon, LinkedinIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import ViewModeToggle from '../UI/ViewModeToggle';
import { DisclosureBadgeGroup } from '../Disclosure/DisclosureBadgeGroup';
import type { DisclosureId } from '../../data/disclosures';
import { ProfileData } from './ProfileLayout';
// --- Types ---
export interface PortfolioItem {
  id: string;
  title: string;
  category: string;
  imageUrl: string;
  description: string;
  aspectRatio: 'portrait' | 'landscape' | 'square';
  serviceCategory: string;
}
export interface ServiceTier {
  id: string;
  name: string;
  price: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  turnaround: string;
  revisions: string;
  category: string;
}
export interface Review {
  id: string;
  clientName: string;
  clientRole: string;
  clientAvatar: string;
  rating: number;
  comment: string;
  date: string;
  projectType: string;
  completionTime: string;
  /**
   * Commercial relationship behind this review. Note that an ordinary client
   * who PAID this professional is not making a "Paid Review" — they get
   * `purchased`. `paid-review` means the reviewer was paid to review.
   */
  disclosures?: DisclosureId[];
}
export interface Event {
  id: string;
  title: string;
  type: string;
  date: string;
  time: string;
  location: string;
  description: string;
}
export interface ProcessStep {
  title: string;
  description: string;
}
// --- Components ---
export const PortfolioTab = ({
  items,
  services
}: {
  items: PortfolioItem[];
  services?: ServiceTier[];
}) => {
  const [filter, setFilter] = useState('All');
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const categories = ['All', ...Array.from(new Set(items.map(i => i.category)))];
  const filteredItems = filter === 'All' ? items : items.filter(item => item.category === filter);
  // Helper to get pricing info for a category
  const getServiceInfo = (category: string) => {
    if (!services) return null;
    const categoryServices = services.filter(s => s.category === category);
    if (categoryServices.length === 0) return null;
    // Find cheapest service
    const cheapest = categoryServices.reduce((prev, curr) => {
      const prevPrice = parseInt(prev.price.replace(/[^0-9]/g, ''));
      const currPrice = parseInt(curr.price.replace(/[^0-9]/g, ''));
      return prevPrice < currPrice ? prev : curr;
    });
    return {
      price: cheapest.price,
      turnaround: cheapest.turnaround
    };
  };
  return <div className="space-y-8" data-id="element-3116" data-mp="rwwhmsyhnr55">
      {/* Filter Chips */}
      <div className="flex flex-wrap gap-2" data-id="element-3117" data-mp="123z3hhj7nfp2">
        {categories.map(cat => <button key={cat} onClick={() => setFilter(cat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === cat ? 'bg-purple-600 text-white shadow-md' : dk.isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`} data-id="element-3118" data-mp="x31nnbloaxqq">
            {cat}
          </button>)}
      </div>

      {/* Masonry Grid */}
      <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[200px]" data-id="element-3119" data-mp="1fwx4pkic420j">
        <AnimatePresence data-id="element-3120" data-mp="172skvl1mbqui">
          {filteredItems.map(item => {
          const serviceInfo = getServiceInfo(item.serviceCategory);
          return <motion.div layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.2
          }} key={item.id} className={`group relative cursor-pointer rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ${item.aspectRatio === 'portrait' ? 'row-span-2' : item.aspectRatio === 'landscape' ? 'col-span-1' : 'col-span-1'}`} onClick={() => setSelectedItem(item)} data-id="element-3121" data-mp="23m9o1d7dlqgk">
                <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-3122" data-mp="1t7ih2iv79h3z" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6" data-id="element-3123" data-mp="2pr95jlb24zpe">
                  <span className="text-purple-300 text-xs font-bold uppercase tracking-wider mb-1" data-id="element-3124" data-mp="9dz0mgbk2wii">
                    {item.category}
                  </span>
                  <h3 className="text-white font-bold text-lg mb-2" data-id="element-3125" data-mp="o4eipm8odocg">
                    {item.title}
                  </h3>

                  {serviceInfo && <div className="flex items-center gap-3 text-xs text-gray-300 border-t border-white/20 pt-2" data-id="element-3126" data-mp="3rjtrkk5bhrw0">
                      <div className="flex items-center gap-1" data-id="element-3127" data-mp="3mlilmki581j2">
                        <DollarSignIcon className="w-3 h-3" data-id="element-3128" data-mp="5c3xr7dz8b2m" />
                        From {serviceInfo.price}
                      </div>
                      <div className="flex items-center gap-1" data-id="element-3129" data-mp="bfwlsw1aduo">
                        <ClockIcon className="w-3 h-3" data-id="element-3130" data-mp="1gq7p05t6jl3z" />~
                        {serviceInfo.turnaround}
                      </div>
                    </div>}
                </div>
              </motion.div>;
        })}
        </AnimatePresence>
      </motion.div>

      {/* Inline Lightbox Modal */}
      <AnimatePresence data-id="element-3131" data-mp="1nsmghx1osrse">
        {selectedItem && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" onClick={() => setSelectedItem(null)} data-id="element-3132" data-mp="1ssqxrpaf5qjo">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className={`relative max-w-5xl w-full max-h-[90vh] rounded-2xl overflow-hidden flex flex-col md:flex-row ${dk.isDark ? 'bg-gray-900' : 'bg-white'}`} onClick={e => e.stopPropagation()} data-id="element-3133" data-mp="1a2sm6z7exfba">
              <button onClick={() => setSelectedItem(null)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" data-id="element-3134" data-mp="2vn7iihiegkqc">
                <XIcon className="w-6 h-6" data-id="element-3135" data-mp="2lngp6j5ksbru" />
              </button>

              <div className="flex-1 bg-black flex items-center justify-center p-4" data-id="element-3136" data-mp="2gpng9icw13nv">
                <img src={selectedItem.imageUrl} alt={selectedItem.title} className="max-w-full max-h-[80vh] object-contain" data-id="element-3137" data-mp="2bxnavae1tijw" />
              </div>

              <div className="w-full md:w-80 p-8 flex flex-col overflow-y-auto" data-id="element-3138" data-mp="22yidw8i4xx9u">
                <div className="mb-6" data-id="element-3139" data-mp="1xydxeih892jm">
                  <span className={`inline-block px-3 py-1 ${rt.badgeRadius} text-xs font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 mb-3`} data-id="element-3140" data-mp="1xxciu4105xx5">
                    {selectedItem.category}
                  </span>
                  <h2 className={`text-2xl font-bold mb-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3141" data-mp="3luabvfcnx0kz">
                    {selectedItem.title}
                  </h2>
                  <p className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3142" data-mp="3csau2kttpsgz">
                    {selectedItem.description}
                  </p>
                </div>

                {(() => {
              const serviceInfo = getServiceInfo(selectedItem.serviceCategory);
              if (!serviceInfo) return null;
              return <div className={`mb-6 p-4 rounded-lg ${dk.isDark ? 'bg-gray-800' : 'bg-gray-50'}`} data-id="element-3143" data-mp="2f5vdrmd4v0v0">
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3144" data-mp="2k4oqvdoicpun">
                        Estimated Cost
                      </h4>
                      <div className="flex justify-between items-center mb-2" data-id="element-3145" data-mp="2aidse9xfwmcs">
                        <span className={`text-sm ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3146" data-mp="25irerzdp943g">
                          Starting Price
                        </span>
                        <span className={`font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3147" data-mp="2fhqq5ppundu5">
                          {serviceInfo.price}
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-3148" data-mp="2obc1ez2t2cyt">
                        <span className={`text-sm ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3149" data-mp="3371g0c5nvnti">
                          Turnaround
                        </span>
                        <span className={`font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3150" data-mp="ztuopmogvc2s">
                          {serviceInfo.turnaround}
                        </span>
                      </div>
                    </div>;
            })()}

                <div className="mt-auto space-y-3" data-id="element-3151" data-mp="1iqdr7t4rm1qd">
                  <button className="w-full py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2" data-id="element-3152" data-mp="1iq40g3bwbqjm">
                    <MailIcon className="w-4 h-4" data-id="element-3153" data-mp="19omlioa85uwi" />
                    Inquire About This Style
                  </button>
                  <button className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 border ${dk.isDark ? 'border-gray-700 hover:bg-gray-800 text-gray-300' : 'border-gray-200 hover:bg-gray-50 text-gray-700'}`} data-id="element-3154" data-mp="14ql4sa09u7o7">
                    <Share2Icon className="w-4 h-4" data-id="element-3155" data-mp="250vw2c3uug0h" />
                    Share Project
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export const ServicesTab = ({
  services
}: {
  services: ServiceTier[];
}) => {
  const dk = useDarkClasses();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {} as Record<string, ServiceTier[]>);
  return <div className="space-y-8" data-id="element-3156" data-mp="31mz4tycy0l24">
      {/* View Toggle Header */}
      <div className="flex justify-end" data-id="element-3157" data-mp="13ba1pjtt8sxh">
        <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} data-id="element-3158" data-mp="17bamdm7i60cz" />
      </div>

      {Object.entries(groupedServices).map(([category, categoryServices]) => <div key={category} className="space-y-4" data-id="element-3159" data-mp="2amge1n8ndpoo">
          <h3 className={`text-lg font-bold flex items-center gap-3 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3160" data-mp="25nmylp9svolv">
            {category}
            <div className={`h-px flex-1 ${dk.isDark ? 'bg-gray-800' : 'bg-gray-200'}`} data-id="element-3161" data-mp="2jkjptrttz83a" />
          </h3>

          {viewMode === 'grid' ? <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3162" data-mp="2tjhm5gxi05wu">
              {categoryServices.map(tier => <div key={tier.id} className={`relative rounded-2xl p-6 border transition-all duration-300 flex flex-col ${tier.isPopular ? dk.isDark ? 'bg-gray-800 border-purple-500 shadow-lg shadow-purple-900/20' : 'bg-white border-purple-500 shadow-xl shadow-purple-100' : dk.isDark ? 'bg-gray-800/50 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-purple-200'}`} data-id="element-3163" data-mp="2ojv9p9phutp3">
                  {tier.isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-purple-600 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-sm" data-id="element-3164" data-mp="3qz25xm8208re">
                      Most Popular
                    </div>}

                  <div className="mb-6 text-center" data-id="element-3165" data-mp="3ly4nd8j4tcpd">
                    <h3 className={`text-lg font-bold mb-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3166" data-mp="3m10m2z2t05ev">
                      {tier.name}
                    </h3>
                    <div className={`text-3xl font-bold mb-2 ${dk.isDark ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-3167" data-mp="2y9lb6hwyofs0">
                      {tier.price}
                    </div>
                    <p className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3168" data-mp="2p8n7y1uckcah">
                      {tier.description}
                    </p>
                  </div>

                  <div className={`space-y-4 mb-8 flex-1`} data-id="element-3169" data-mp="2m5zaqoaks3vc">
                    {tier.features.map((feature, i) => <div key={i} className="flex items-start gap-3 text-sm" data-id="element-3170" data-mp="2h8za4swhvtbm" data-mpi={i}>
                        <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-3171" data-mp="2cfxv8kwafts1" />
                        <span className={dk.isDark ? 'text-gray-300' : 'text-gray-600'} data-id="element-3172" data-mp="28euii79jedv1">
                          {feature}
                        </span>
                      </div>)}
                  </div>

                  <div className={`pt-6 border-t ${dk.isDark ? 'border-gray-700' : 'border-gray-100'} space-y-4`} data-id="element-3173" data-mp="1tkplzkbt6thb">
                    <div className="flex justify-between text-xs font-medium" data-id="element-3174" data-mp="3nvmilddgeutd">
                      <span className={dk.isDark ? 'text-gray-400' : 'text-gray-500'} data-id="element-3175" data-mp="3juj3it38d2t7">
                        Turnaround
                      </span>
                      <span className={dk.isDark ? 'text-white' : 'text-gray-900'} data-id="element-3176" data-mp="1akjq6xfnha7i">
                        {tier.turnaround}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs font-medium" data-id="element-3177" data-mp="mofbvfz32hps">
                      <span className={dk.isDark ? 'text-gray-400' : 'text-gray-500'} data-id="element-3178" data-mp="mqicetxv3t8r">
                        Revisions
                      </span>
                      <span className={dk.isDark ? 'text-white' : 'text-gray-900'} data-id="element-3179" data-mp="10ogp7uwjbhm5">
                        {tier.revisions}
                      </span>
                    </div>

                    <button className={`w-full py-3 rounded-lg font-bold transition-colors ${tier.isPopular ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-md' : dk.isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-purple-50 hover:bg-purple-100 text-purple-700'}`} data-id="element-3180" data-mp="2c22fjje4rp99">
                      Select Package
                    </button>
                  </div>
                </div>)}
            </div> : <div className="space-y-3" data-id="element-3181" data-mp="3ph24duwbkqm2">
              {categoryServices.map(tier => <div key={tier.id} className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border ${tier.isPopular ? dk.isDark ? 'bg-gray-800 border-purple-500/50' : 'bg-white border-purple-200' : dk.isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3182" data-mp="3khp70g7to158">
                  <div className="flex-1" data-id="element-3183" data-mp="36ng18b7fi31l">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3184" data-mp="31r7rxefwx42c">
                      <h4 className={`font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3185" data-mp="1h8dqu54ropmm">
                        {tier.name}
                      </h4>
                      {tier.isPopular && <span className="px-2 py-0.5 text-[10px] uppercase font-bold bg-purple-600 text-white rounded-full" data-id="element-3186" data-mp="1890e93lcjkxo">
                          Popular
                        </span>}
                    </div>
                    <p className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3187" data-mp="1zx290jbv8gup">
                      {tier.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-6 sm:gap-8" data-id="element-3188" data-mp="1vws3f7rzjsgd">
                    <div className="text-right" data-id="element-3189" data-mp="1vz4j1ea9tqq9">
                      <div className={`font-bold ${dk.isDark ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-3190" data-mp="fi08oo7da93w">
                        {tier.price}
                      </div>
                      <div className={`text-xs ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-3191" data-mp="1b9rja8fopw2f">
                        {tier.turnaround}
                      </div>
                    </div>
                    <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tier.isPopular ? 'bg-purple-600 hover:bg-purple-700 text-white' : dk.isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-purple-50 hover:bg-purple-100 text-purple-700'}`} data-id="element-3192" data-mp="x8f3py4frwh2">
                      Select
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>)}
    </div>;
};
export const ReviewsTab = ({
  reviews
}: {
  reviews: Review[];
}) => {
  const dk = useDarkClasses();
  const averageRating = reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length;
  return <div className="space-y-8" data-id="element-3193" data-mp="2g74p4wdmvyyd">
      {/* Summary Header */}
      <div className={`p-6 rounded-2xl border flex flex-col sm:flex-row items-center justify-between gap-6 ${dk.isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3194" data-mp="2g8xv09gfaf5b">
        <div className="flex items-center gap-4" data-id="element-3195" data-mp="2q3zlb8mfpn9h">
          <div className="text-center" data-id="element-3196" data-mp="2v4x2pizmed9x">
            <div className={`text-4xl font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3197" data-mp="2v5ylk9mvo427">
              {averageRating.toFixed(1)}
            </div>
            <div className="flex text-yellow-400 text-xs mt-1" data-id="element-3198" data-mp="343r4dnrt5tuc">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-3 h-3 ${i < Math.round(averageRating) ? 'fill-current' : 'text-gray-300'}`} data-id="element-3199" data-mp="38wah6iio8ky3" data-mpi={i} />)}
            </div>
          </div>
          <div className={`h-12 w-px ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-3200" data-mp="3is54hqxub477" />

          <div data-id="element-3201" data-mp="q69dxvzz2js6">
            <div className={`text-sm font-medium ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3202" data-mp="q6sw9dtjba6q">
              Overall Rating
            </div>
            <div className={`text-xs ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3203" data-mp="v775v0asas6v">
              Based on {reviews.length}{' '}
              {reviews.length === 1 ? 'review' : 'reviews'}
            </div>
          </div>
        </div>

        <button className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${dk.isDark ? 'border-gray-600 hover:bg-gray-700 text-white' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`} data-id="element-3204" data-mp="194lz6g45rk44">
          Write a Review
        </button>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3205" data-mp="1nt8dcei1u5tr">
        {reviews.map((review, idx) => <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: idx * 0.1
      }} key={review.id} className={`p-6 rounded-2xl border flex flex-col h-full ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3206" data-mp="1rxg1oaklhjrg" data-mpi={idx}>
            <div className="flex items-start justify-between mb-4" data-id="element-3207" data-mp="33div7v5zjmkt">
              <div className="flex items-center gap-3" data-id="element-3208" data-mp="33fbwsw1qrgly">
                <img src={review.clientAvatar} alt={review.clientName} className="w-10 h-10 rounded-full object-cover" data-id="element-3209" data-mp="3wkjqp2ypf5h" />

                <div className="min-w-0" data-id="element-3210" data-mp="3r9n590firswq">
                  <h4 className={`font-bold ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3211" data-mp="3r8lihyp55hz6">
                    {review.clientName}
                  </h4>
                  <p className={`text-xs ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3212" data-mp="nno4xf1l4ea0">
                    {review.clientRole}
                  </p>
                  {/* Commercial disclosure for a bookish service */}
                  <DisclosureBadgeGroup disclosures={review.disclosures} subjectType="service" className="mt-1" data-id="element-3213" data-mp="16p55a3z0j9bz" />
                </div>
              </div>
              <div className={`px-2 py-1 rounded-md text-xs font-medium ${dk.isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-700'}`} data-id="element-3214" data-mp="wykaysqdgqyv">
                {review.projectType}
              </div>
            </div>

            <div className="flex text-yellow-400 mb-3" data-id="element-3215" data-mp="a1imh8uv3ph0">
              {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-current' : 'text-gray-300'}`} data-id="element-3216" data-mp="e2kfv923l7b2" data-mpi={i} />)}
            </div>

            <p className={`text-sm mb-4 italic flex-1 ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3217" data-mp="28eik0wljvd03">
              "{review.comment}"
            </p>

            <div className={`flex items-center justify-between text-xs pt-4 border-t ${dk.isDark ? 'border-gray-700 text-gray-500' : 'border-gray-100 text-gray-400'}`} data-id="element-3218" data-mp="3oxals3efna3p">
              <span data-id="element-3219" data-mp="1qib3ik0jcml">{review.date}</span>
              <span className="flex items-center gap-1" data-id="element-3220" data-mp="1pglm9292nlc">
                <ClockIcon className="w-3 h-3" data-id="element-3221" data-mp="gfefn5mv27ig" />
                Completed in {review.completionTime}
              </span>
            </div>
          </motion.div>)}
      </div>
    </div>;
};
export const AboutTab = ({
  profile,
  skills,
  tools,
  processSteps,
  willDo,
  wontDo,
  inspiration,
  funFacts
}: AboutTabProps) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-3222" data-mp="2r9a8l8nygngp">
      {/* Sidebar */}
      <div className="space-y-6" data-id="element-3223" data-mp="2ma7f8lhc85ok">
        {skills && <div className={`p-6 rounded-2xl border shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3224" data-mp="2mbsakdbh7snh">
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3225" data-mp="2socvny2zygs0">
              <LayersIcon className="w-5 h-5 text-purple-500" data-id="element-3226" data-mp="32nu4uphgjcsq" />
              Skills
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-3227" data-mp="250d8f84w98i6">
              {skills.map(skill => <span key={skill} className={`px-3 py-1 ${rt.badgeRadius} text-xs font-medium ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-3228" data-mp="29z6ck9yajn5o">
                  {skill}
                </span>)}
            </div>
          </div>}

        {tools && <div className={`p-6 rounded-2xl border shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3229" data-mp="1m8spyyyh6gih">
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3230" data-mp="1jppfn6ykj6lj">
              <PenToolIcon className="w-5 h-5 text-pink-500" data-id="element-3231" data-mp="1equlj8v5hvm4" />
              Tools
            </h3>
            <div className="flex flex-wrap gap-2" data-id="element-3232" data-mp="19p5aht7xhhy1">
              {tools.map(tool => <span key={tool} className={`px-3 py-1 ${rt.badgeRadius} text-xs font-medium ${dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-3233" data-mp="14rdhevvjrps9">
                  {tool}
                </span>)}
            </div>
          </div>}

        {profile.social && <div className={`p-6 rounded-2xl border shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3234" data-mp="m1xfq584lra9">
            <h3 className={`text-lg font-bold mb-4 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3235" data-mp="h4f2i2n2spwg">
              Connect
            </h3>
            <div className="space-y-3" data-id="element-3236" data-mp="2euawt3nqi9j">
              {profile.social.website && <a href={profile.social.website} target="_blank" rel="noreferrer" className={`flex items-center gap-3 text-sm transition-colors ${dk.isDark ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`} data-id="element-3237" data-mp="7frpkvpgku6a">
                  <GlobeIcon className="w-5 h-5" data-id="element-3238" data-mp="m4effrcpp4br" />
                  Website
                </a>}
              {profile.social.instagram && <a href="#" className={`flex items-center gap-3 text-sm transition-colors ${dk.isDark ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`} data-id="element-3239" data-mp="cel4mske8t8k">
                  <InstagramIcon className="w-5 h-5" data-id="element-3240" data-mp="1515utpp4uzaj" />
                  Instagram
                </a>}
              {profile.social.twitter && <a href="#" className={`flex items-center gap-3 text-sm transition-colors ${dk.isDark ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`} data-id="element-3241" data-mp="1j77hbmojzyg3">
                  <TwitterIcon className="w-5 h-5" data-id="element-3242" data-mp="1sx0rcj91s5i8" />
                  Twitter / X
                </a>}
              {profile.social.behance && <a href="#" className={`flex items-center gap-3 text-sm transition-colors ${dk.isDark ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`} data-id="element-3243" data-mp="108d6gos7ni57">
                  <PaletteIcon className="w-5 h-5" data-id="element-3244" data-mp="1a580audjgud5" />
                  Behance
                </a>}
              {profile.social.linkedin && <a href="#" className={`flex items-center gap-3 text-sm transition-colors ${dk.isDark ? 'text-gray-400 hover:text-purple-400' : 'text-gray-600 hover:text-purple-600'}`} data-id="element-3245" data-mp="2llesv8zchjyj">
                  <LinkedinIcon className="w-5 h-5" data-id="element-3246" data-mp="ew4f8mwg9d5f" />
                  LinkedIn
                </a>}
            </div>
          </div>}
      </div>

      {/* Main Content */}
      <div className="lg:col-span-2 space-y-8" data-id="element-3247" data-mp="nxkqsxz03lb8">
        <div className={`p-8 rounded-2xl border shadow-sm ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3248" data-mp="sq5jfw667sd6">
          <h3 className={`text-xl font-bold mb-4 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3249" data-mp="xqt7qa30lv3p">
            About Me
          </h3>
          <p className={`leading-relaxed mb-8 ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3250" data-mp="16qg9wy3kj4xt">
            {profile.bio}
          </p>

          {/* Process Timeline */}
          {processSteps && <div className="mb-10" data-id="element-3251" data-mp="38ikx5wa4he27">
              <h4 className={`text-lg font-bold mb-6 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3252" data-mp="3dfus1rga6hxf">
                My Process
              </h4>
              <div className="relative" data-id="element-3253" data-mp="35yeqvhy1dbih">
                {/* Connecting Line */}
                <div className={`absolute top-4 left-0 right-0 h-0.5 ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-3254" data-mp="314alh34f5bji" />

                <div className="grid grid-cols-5 gap-2 relative" data-id="element-3255" data-mp="2r7xsk7gp71pt">
                  {processSteps.map((step, idx) => <div key={idx} className="flex flex-col items-center text-center group" data-id="element-3256" data-mp="3ou4uf9lmafyh" data-mpi={idx}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mb-3 relative z-10 transition-colors ${dk.isDark ? 'bg-gray-800 border-2 border-purple-500 text-purple-400 group-hover:bg-purple-900' : 'bg-white border-2 border-purple-500 text-purple-600 group-hover:bg-purple-50'}`} data-id="element-3257" data-mp="3ex0brjqzd2mf">
                        {idx + 1}
                      </div>
                      <h5 className={`text-xs font-bold mb-1 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3258" data-mp="y8np0tjdt1gv">
                        {step.title}
                      </h5>
                      <p className={`text-[10px] leading-tight px-1 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3259" data-mp="17yqieuuf0hfq">
                        {step.description}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>}

          {/* Commission Boundaries */}
          {(willDo || wontDo) && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-3260" data-mp="1qtxc7t2i23ss">
              {willDo && <div className={`p-5 rounded-xl ${dk.isDark ? 'bg-green-900/10 border border-green-900/30' : 'bg-green-50 border border-green-100'}`} data-id="element-3261" data-mp="3snmyou6bck1d">
                  <h4 className={`text-sm font-bold mb-3 flex items-center gap-2 ${dk.isDark ? 'text-green-400' : 'text-green-700'}`} data-id="element-3262" data-mp="3nqwj07yf7qgh">
                    <CheckCircleIcon className="w-4 h-4" data-id="element-3263" data-mp="2852qbg7lkqv1" />
                    Will Do
                  </h4>
                  <ul className="space-y-2" data-id="element-3264" data-mp="1tgg02jgfmep1">
                    {willDo.map((item, i) => <li key={i} className={`text-sm flex items-start gap-2 ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3265" data-mp="1ydzo2b2xm5u6" data-mpi={i}>
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-green-500 flex-shrink-0" data-id="element-3266" data-mp="1pgfm0zx8w5ii" />
                        {item}
                      </li>)}
                  </ul>
                </div>}

              {wontDo && <div className={`p-5 rounded-xl ${dk.isDark ? 'bg-red-900/10 border border-red-900/30' : 'bg-red-50 border border-red-100'}`} data-id="element-3267" data-mp="34wxw4ig4afb5">
                  <h4 className={`text-sm font-bold mb-3 flex items-center gap-2 ${dk.isDark ? 'text-red-400' : 'text-red-700'}`} data-id="element-3268" data-mp="38ebswb18se74">
                    <XCircleIcon className="w-4 h-4" data-id="element-3269" data-mp="33h2858ossqpm" />
                    Won't Do
                  </h4>
                  <ul className="space-y-2" data-id="element-3270" data-mp="2ue1195s3yvkw">
                    {wontDo.map((item, i) => <li key={i} className={`text-sm flex items-start gap-2 ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3271" data-mp="2ugdtqcvhsoc7" data-mpi={i}>
                        <span className="mt-1.5 w-1 h-1 rounded-full bg-red-500 flex-shrink-0" data-id="element-3272" data-mp="3m7joedsg1tu3" />
                        {item}
                      </li>)}
                  </ul>
                </div>}
            </div>}

          {inspiration && <>
              <h4 className={`text-lg font-bold mb-3 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3273" data-mp="8yovpypaiwx5">
                Inspiration
              </h4>
              <div className="flex flex-wrap gap-2 mb-6" data-id="element-3274" data-mp="1rnrsqvn1rg81">
                {inspiration.map(item => <span key={item} className={`px-3 py-1 rounded-md text-sm ${dk.isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-700'}`} data-id="element-3275" data-mp="1wmcs6suh5vvf">
                    {item}
                  </span>)}
              </div>
            </>}

          {funFacts && <>
              <h4 className={`text-lg font-bold mb-3 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3276" data-mp="2fhjbgx4xnzu8">
                Fun Facts
              </h4>
              <ul className={`space-y-2 ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3277" data-mp="2oaed7gv6zdf9">
                {funFacts.map((fact, i) => <li key={i} className="flex items-start gap-2" data-id="element-3278" data-mp="2y9c4grk05l1y" data-mpi={i}>
                    <span className="text-purple-500 mt-1.5" data-id="element-3279" data-mp="2y7ixfb5h8ytf">•</span>
                    {fact}
                  </li>)}
              </ul>
            </>}
        </div>
      </div>
    </div>;
};
export const EventsTab = ({
  events
}: {
  events: Event[];
}) => {
  const dk = useDarkClasses();
  return <div className="space-y-4 max-w-4xl" data-id="element-3280" data-mp="2pdbp0t83aqer">
      {events.map(event => <div key={event.id} className={`flex flex-col sm:flex-row sm:items-center gap-6 p-6 rounded-xl border ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-3281" data-mp="2pg7q2qf8hv7f">
          <div className={`w-16 h-16 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${dk.isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`} data-id="element-3282" data-mp="2bm6z4iuixchm">
            <span className="text-xs font-bold uppercase" data-id="element-3283" data-mp="3n0u8oz3ahcxj">
              {event.date.split(' ')[0]}
            </span>
            <span className="text-xl font-bold" data-id="element-3284" data-mp="3i1r1b7zu6ume">
              {event.date.split(' ')[1].replace(',', '')}
            </span>
          </div>
          <div className="flex-1" data-id="element-3285" data-mp="3poefeg4hbem2">
            <div className={`text-sm font-medium mb-1 ${dk.isDark ? 'text-purple-400' : 'text-purple-600'}`} data-id="element-3286" data-mp="3ulelgr1bwhd6">
              {event.type}
            </div>
            <h3 className={`text-lg font-bold mb-2 ${dk.isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-3287" data-mp="3kmp18rlymktr">
              {event.title}
            </h3>
            <p className={`text-sm mb-3 ${dk.isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-3288" data-mp="c98a3azpulzs">
              {event.description}
            </p>
            <div className={`flex flex-wrap gap-4 text-sm ${dk.isDark ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-3289" data-mp="3e23i9smfzw7">
              <div className="flex items-center gap-1.5" data-id="element-3290" data-mp="7e2hxufq0b39">
                <ClockIcon className="w-4 h-4" data-id="element-3291" data-mp="v57w8560ivbg" />
                {event.time}
              </div>
              <div className="flex items-center gap-1.5" data-id="element-3292" data-mp="zxj886e127kd">
                <MapPinIcon className="w-4 h-4" data-id="element-3293" data-mp="19sgcvli3bsut" />
                {event.location}
              </div>
            </div>
          </div>
          <button className="px-6 py-2.5 rounded-lg font-medium bg-purple-600 text-white hover:bg-purple-700 transition-colors" data-id="element-3294" data-mp="15s63rzz04mbq">
            Register
          </button>
        </div>)}
    </div>;
};